import type {
    ApiCommerceStandaloneOfferV2,
    ApiCommerceStandaloneSnapshotV2,
} from "@bridge-applications/pb-types";
import type {
    BundleOfferArtwork,
    BundleOfferImages,
} from "./bundleOfferPresentation.ts";
import { telegramStarsPrice } from "./commerce.ts";
import { commerceOrderClientAction } from "./commerceCheckout.ts";

export function standaloneCheckoutOrder(offer: ApiCommerceStandaloneOfferV2) {
    const order = offer.activeOrder;
    if (!order) return null;
    const action = commerceOrderClientAction(order.status);
    return action === "FAILED" || action === "SUCCEEDED" ? null : order;
}

/** Display the amount of the checkout the server will resume, even on an older revision. */
export function standaloneOfferPrice(offer: ApiCommerceStandaloneOfferV2) {
    const price = telegramStarsPrice(offer);
    const order = standaloneCheckoutOrder(offer);
    if (!price || !order) return price;
    if (
        order.providerCode !== price.providerCode ||
        order.paymentCurrencyCode !== price.paymentCurrencyCode
    )
        return null;
    return { ...price, unitAmount: order.paymentAmount };
}

export function standaloneOfferAvailability(
    offer: ApiCommerceStandaloneOfferV2 | null,
    now: number,
): "available" | "expired" | "unavailable" {
    if (!offer || !standaloneOfferPrice(offer)) return "unavailable";
    // A checkout already issued by the server remains resumable across window/revision changes.
    if (standaloneCheckoutOrder(offer)) return "available";
    if (offer.endsAt && Date.parse(offer.endsAt) <= now) return "expired";
    if (offer.windowEndsAt && Date.parse(offer.windowEndsAt) <= now)
        return "expired";
    return offer.canPurchase &&
        offer.remainingPurchaseCount !== 0 &&
        Date.parse(offer.eligibleAt) <= now &&
        (!offer.startsAt || Date.parse(offer.startsAt) <= now)
        ? "available"
        : "unavailable";
}

export function standalonePresentationKey(
    offer: ApiCommerceStandaloneOfferV2,
): string {
    return `${offer.offerRevisionId}:${offer.windowStartsAt ?? "always"}`;
}

/** One wake-up at the next eligibility/window boundary; countdowns never poll the API. */
export function nextStandaloneBoundary(
    snapshot: ApiCommerceStandaloneSnapshotV2,
    now: number,
): number | null {
    const boundaries = snapshot.offers.flatMap((offer) =>
        offer.remainingPurchaseCount === 0
            ? []
            : [
                  offer.eligibleAt,
                  offer.startsAt,
                  offer.endsAt,
                  offer.windowEndsAt,
              ]
                  .flatMap((value) => (value ? [Date.parse(value)] : []))
                  .filter((value) => value > now),
    );
    return boundaries.length ? Math.min(...boundaries) : null;
}

export function standaloneImageUrls(
    images: Readonly<Record<string, { url: string }>>,
): BundleOfferImages {
    if (!images.en)
        throw new Error("Offer artwork requires an English fallback");
    return {
        ...Object.fromEntries(
            Object.entries(images).map(([locale, image]) => [
                locale,
                image.url,
            ]),
        ),
        en: images.en.url,
    };
}

export function standaloneBundleArtwork(
    artwork: Extract<
        NonNullable<ApiCommerceStandaloneOfferV2["presentation"]["dialog"]>,
        { kind: "BUNDLE" }
    >["artwork"],
): BundleOfferArtwork {
    return {
        ...artwork,
        header: {
            ...artwork.header,
            images: standaloneImageUrls(artwork.header.images),
        },
        background: {
            ...artwork.background,
            images: standaloneImageUrls(artwork.background.images),
        },
        close: { ...artwork.close, image: artwork.close.image.url },
    };
}
